<template>
  <div
    class="app flex min-h-screen flex-col overflow-hidden font-sans"
    :class="{
      'app--fullscreen-embedded': fullScreenEmbedded,
      'app--fixed-viewport': route.meta?.disablePageScroll,
    }"
  >
    <!-- Mobile overlay: full viewport; sidebar slides above it -->
    <div
      v-if="shouldShowAppShell && showSideNavigation"
      v-show="isSidebarVisible && isMobileView"
      class="mobile-overlay fixed inset-0 z-[50] bg-black/40 backdrop-blur-[1px] transition-opacity duration-300 ease-out"
      aria-hidden="true"
      @click="toggleSideNavigation"
    />

    <!-- Main layout: sidenav (fixed) + column (header + content) -->
    <div class="main-layout flex h-full min-h-0 w-full flex-1 overflow-hidden">
      <!-- Side Navigation: full viewport height, flush left -->
      <SideNavigation
        v-if="shouldShowAppShell && showSideNavigation"
        :key="isMobileView ? 'mobile' : (isCollapsed ? 'desktop-collapsed' : 'desktop-expanded')"
        :is-collapsed="isCollapsed"
        :mobile-menu-open="isSidebarVisible && isMobileView"
        :is-mobile="isMobileView"
        @update:is-collapsed="isCollapsed = $event"
        @update:isCollapsed="isCollapsed = $event"
        @close-mobile-menu="closeMobileMenu"
      />

      <!-- Main column: top bar only above scrollable content -->
      <div
        class="main-column flex min-h-0 min-w-0 w-full flex-1 flex-col"
        :class="[
          shouldShowAppShell && showSideNavigation && !isMobileView
            ? isCollapsed
              ? 'main-column--sidebar-collapsed'
              : 'main-column--sidebar-expanded'
            : '',
        ]"
      >
        <HeaderComponent
          v-if="shouldShowAppShell && showHeaderBar"
          class="sticky top-0 z-40 shrink-0"
          :sidebar-open="isSidebarVisible && isMobileView"
          @toggleSidebar="toggleSideNavigation"
        />

        <!-- Main Content Area -->
        <div
          :class="[
            'main-content relative min-h-0 flex flex-1 w-full flex-col overflow-y-auto bg-gradient-to-r from-gray-100 to-blue-50',
            fullScreenEmbedded ? 'main-content--fullscreen' : '',
            route.meta?.disablePageScroll ? 'main-content--no-page-scroll' : '',
          ]"
        >
          <!-- 
            Keep loader visible while route is loading OR when user is logged out
            but router has not yet redirected to login.
          -->
          <PageLoader
            :visible="loader.loading || !canRenderRouteContent"
            class="absolute inset-0 z-20"
          />

          <!-- 
            Important:
            Use v-if instead of v-show so protected content is destroyed immediately
            after logout and cannot briefly reappear.
          -->
          <div
            v-if="canRenderRouteContent"
            class="router-view-wrapper h-full w-full"
          >
            <!-- Full-screen mode: pill shown briefly (1.5s) then auto-hides; Esc exits when focus is in app -->
            <Transition name="slide-pill">
              <div
                v-if="
                  fullScreenEmbedded && isEmbeddedRoute && showFullScreenPill
                "
                class="embedded-fullscreen-pill"
                role="status"
                aria-live="polite"
              >
                <span class="embedded-fullscreen-pill-text">
                  <i
                    class="fas fa-compress-alt mr-2 opacity-80"
                    aria-hidden="true"
                  />
                  <strong>{{ toolLabel }} </strong> is now full screen
                </span>
                <button
                  ref="exitFullScreenBtnRef"
                  type="button"
                  class="embedded-fullscreen-pill-exit"
                  aria-label="Exit full screen"
                  @click="exitFullScreenEmbedded"
                >
                  Exit Full Screen (Esc)
                </button>
              </div>
            </Transition>

            <!-- Invisible focus trap so Esc works after pill hides -->
            <div
              v-if="fullScreenEmbedded && isEmbeddedRoute"
              ref="fullscreenFocusTrapRef"
              tabindex="-1"
              class="embedded-fullscreen-focus-trap"
              aria-hidden="true"
            />

            <!-- When pill is hidden: exit control in top-right hot corner -->
            <div
              v-if="
                fullScreenEmbedded && isEmbeddedRoute && !showFullScreenPill
              "
              class="embedded-fullscreen-exit-corner-zone"
              aria-label="Exit full screen area"
              @mouseenter="exitCornerRevealed = true"
              @mouseleave="exitCornerRevealed = false"
            >
              <button
                ref="exitCornerBtnRef"
                type="button"
                class="embedded-fullscreen-exit-corner"
                :class="{
                  'embedded-fullscreen-exit-corner--revealed':
                    exitCornerRevealed,
                }"
                aria-label="Exit full screen"
                @click="exitFullScreenEmbedded"
              >
                <i
                  class="fas fa-compress-alt"
                  aria-hidden="true"
                />
                Exit full screen
              </button>
            </div>

            <!-- Normal mode: notice that user can view in full screen -->
            <Transition name="slide-pill">
              <div
                v-if="
                  isEmbeddedRoute &&
                    !fullScreenEmbedded &&
                    orchestrator.activeEmbedded &&
                    showFullscreenHint
                "
                class="embedded-fullscreen-hint-pill"
                role="status"
              >
                <span class="text-sm text-white/95">
                  You can view this tool in full screen.
                </span>
                <button
                  type="button"
                  class="embedded-fullscreen-hint-btn"
                  @click="enterFullScreenEmbedded"
                >
                  Full screen
                </button>
                <button
                  type="button"
                  class="embedded-fullscreen-hint-dismiss"
                  aria-label="Dismiss"
                  @click="dismissFullscreenHint"
                >
                  <i
                    class="fas fa-times"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </Transition>

            <!-- Embedded apps: iframe pool -->
            <EmbeddedAppPool
              v-show="isEmbeddedRoute"
              class="context-view min-h-full"
              :fullscreen="fullScreenEmbedded"
            />

            <!-- Full-screen toggle -->
            <Transition name="fade-slide-right">
              <button
                v-if="
                  isEmbeddedRoute &&
                    !fullScreenEmbedded &&
                    orchestrator.activeEmbedded &&
                    !showFullscreenHint
                "
                key="embedded-fs-btn"
                type="button"
                class="embedded-fullscreen-enter fixed top-20 right-6 z-20 flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800/90 hover:bg-gray-700 text-white text-sm font-medium shadow-lg transition"
                aria-label="View in full screen"
                @click="enterFullScreenEmbedded"
              >
                <i
                  class="fas fa-expand-alt"
                  aria-hidden="true"
                />
                Full screen
              </button>
            </Transition>

            <!-- Internal views -->
            <div
              v-if="!isEmbeddedRoute"
              :key="route.path === '/' ? 'login' : 'app'"
              class="context-view internal-viewport h-full min-h-full w-full min-w-0 flex flex-col"
            >
              <router-view v-slot="{ Component }">
                <transition name="context">
                  <keep-alive
                    :max="5"
                    :exclude="['ReportsV2DashboardView', 'ReportsV2Dashboard', 'SeoReportsCalendarView', 'SeoReportsCalendar', 'ClientFirstEntryView', 'ClientInitialPlanView', 'LeadDetailsView', 'LeadDetails']"
                  >
                    <component
                      :is="Component"
                      :key="getStableRouteKey(route)"
                      class="context-view"
                    />
                  </keep-alive>
                </transition>
              </router-view>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import http from "@/services/http";
import { useRoute } from "vue-router";
import { useLoaderStore } from "@/store/loader";
import { useAppOrchestratorStore } from "@/store/appOrchestrator";
import { useAuthStore } from "@/store/auth";
import { storeToRefs } from "pinia";
import { initializeEcho } from "@/services/echo";
import { usePresenceStore } from "@/store/presence";
import { eventBus } from "@/eventBus";
import { useQueryClient } from "@tanstack/vue-query";
import SideNavigation from "./components/SideNavigation.vue";
import HeaderComponent from "./components/Header.vue";
import PageLoader from "./components/loader/PageLoader.vue";
import EmbeddedAppPool from "./components/EmbeddedAppPool.vue";

const EMBEDDED_ROUTE_NAMES = [
  "CustomAppBySectionAndSlug",
  "CustomAppBySlug",
  "CustomAppDirectSlug",
  "CustomAppById",
];

const MOBILE_BREAKPOINT = 1024;
const RESIZE_DEBOUNCE_MS = 120;

const loader = useLoaderStore();
const orchestrator = useAppOrchestratorStore();
const authStore = useAuthStore();
const queryClient = useQueryClient();
const { isAuthenticated } = storeToRefs(authStore);
const route = useRoute();

const presenceStore = usePresenceStore();
let globalPresenceSubscription = null;

const getStableRouteKey = (r) => {
  return r.meta?.stableRouteKey || (Object.keys(r.params || {}).length ? r.fullPath : (r.name || r.path));
};

const setupGlobalPresence = () => {
  if (globalPresenceSubscription) return;

  const echo = initializeEcho();
  if (!echo) return;

  globalPresenceSubscription = echo.join("global-presence");
  globalPresenceSubscription
    .here((users) => {
      presenceStore.setOnlineUsers(users);
    })
    .joining((user) => {
      presenceStore.addOnlineUser(user);
    })
    .leaving((user) => {
      presenceStore.removeOnlineUser(user);
    })
    .listen(".sidenav.updated", (event) => {
      queryClient.invalidateQueries({ queryKey: ["sidenav"] });
      eventBus.emit("sidenav:updated", event);
    })
    .listen("SidenavUpdated", (event) => {
      queryClient.invalidateQueries({ queryKey: ["sidenav"] });
      eventBus.emit("sidenav:updated", event);
    })
    .error((error) => {
      console.error("Global presence connection failed", error);
    });
};

const tearDownGlobalPresence = () => {
  if (globalPresenceSubscription) {
    const echo = initializeEcho();
    echo?.leave("global-presence");
    globalPresenceSubscription = null;
    presenceStore.setOnlineUsers([]);
  }
};

watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      setupGlobalPresence();
    } else {
      tearDownGlobalPresence();
    }
  },
  { immediate: true }
);

const fullScreenEmbedded = ref(false);
const showFullscreenHint = ref(true);
const showFullScreenPill = ref(false);
const exitFullScreenBtnRef = ref(null);
const exitCornerBtnRef = ref(null);
const fullscreenFocusTrapRef = ref(null);
const exitCornerRevealed = ref(false);

let fullScreenPillHideTimer = null;
let hintAutoHideTimer = null;
const isCollapsed = ref(false);
const screenWidth = ref(
  typeof window !== "undefined" ? window.innerWidth : 1024
);

const isMobileView = computed(() => screenWidth.value < MOBILE_BREAKPOINT);

// Sidebar visible on desktop by default; hidden on mobile until user opens menu
const isSidebarVisible = ref(screenWidth.value >= MOBILE_BREAKPOINT);

const isLoginRoute = computed(() => route.path === "/");
const isPublicRoute = computed(() => route.meta?.isPublic === true);

/**
 * Allows rendering only:
 * - authenticated app routes
 * - login and explicitly public routes
 *
 * This prevents protected pages from flashing after logout while router redirect is still pending.
 */
const canRenderRouteContent = computed(() => {
  return (
    isAuthenticated.value || isLoginRoute.value || isPublicRoute.value
  );
});

/**
 * Controls authenticated shell elements only.
 * Sidebar/header should never show during logout or login.
 */
const shouldShowAppShell = computed(() => {
  return (
    isAuthenticated.value && !isLoginRoute.value && !fullScreenEmbedded.value
  );
});

let resizeTimeout = null;

function updateWidth() {
  const w = window.innerWidth;

  if (resizeTimeout) clearTimeout(resizeTimeout);

  resizeTimeout = setTimeout(() => {
    resizeTimeout = null;

    requestAnimationFrame(() => {
      screenWidth.value = w;

      if (w >= MOBILE_BREAKPOINT) {
        isSidebarVisible.value = true;
      } else {
        isSidebarVisible.value = false;
      }
    });
  }, RESIZE_DEBOUNCE_MS);
}

/**
 * Allowed origins for microapp SSO messages.
 * Must match the origins where embedded microapps are served.
 */
const MICROAPP_SSO_ALLOWED_ORIGINS = [
  "https://socialmedia.proweaver.com",
  "http://localhost:5173",
  "http://127.0.0.1:5173",
  ...(process.env.VUE_APP_MICROAPP_SSO_ALLOWED_ORIGINS || "")
    .split(",")
]
  .map((origin) => origin.trim())
  .filter(Boolean)
  .filter((origin, index, origins) => origins.indexOf(origin) === index);

let activeSsoTokenPromise = null;

/**
 * Handles postMessage events from embedded microapp iframes.
 * When a microapp sends READY_FOR_SSO, we fetch a signed JWT from dmsuite-api
 * and post it back as SSO_GRANT so the microapp can exchange it for its own session.
 */
async function handleMicroappMessage(event) {
  // Validate origin
  if (!MICROAPP_SSO_ALLOWED_ORIGINS.includes(event.origin)) {
    return;
  }

  // Only handle READY_FOR_SSO from microapp-shell
  if (
    !event.data ||
    event.data.source !== "microapp-shell" ||
    event.data.type !== "READY_FOR_SSO"
  ) {
    return;
  }

  // Only respond if the user is authenticated in DMSuite
  if (!isAuthenticated.value) {
    return;
  }

  try {
    if (!activeSsoTokenPromise) {
      activeSsoTokenPromise = http.get("/api/sso/token").finally(() => {
        activeSsoTokenPromise = null;
      });
    }

    const { data } = await activeSsoTokenPromise;

    if (!data?.token || !event.source) {
      return;
    }

    event.source.postMessage(
      {
        source: "dm-suite",
        type: "SSO_GRANT",
        token: data.token,
      },
      event.origin
    );
  } catch (err) {
    console.error("Failed to generate SSO token for microapp iframe", err);
  }
}


onMounted(() => {
  updateWidth();
  window.addEventListener("resize", updateWidth);
  window.addEventListener("keydown", onKeydown, true);
  window.addEventListener("message", handleMicroappMessage);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
  window.removeEventListener("keydown", onKeydown, true);
  window.removeEventListener("message", handleMicroappMessage);

  if (resizeTimeout) clearTimeout(resizeTimeout);
  if (hintAutoHideTimer) clearTimeout(hintAutoHideTimer);

  clearFullScreenPillTimer();
  tearDownGlobalPresence();
});

// Show header/sidebar if route is not login and user is allowed to see app shell
const showSideNavigation = computed(() => {
  if (isLoginRoute.value) return false;
  if (authStore.accountType === "client" && authStore.clientProfile?.first_entry?.required && !isPublicRoute.value) return false;
  if (authStore.isTaskOnlyInternalUser) return false;
  return true;
});

const showHeaderBar = computed(() => {
  if (isLoginRoute.value) return false;
  if (authStore.accountType === "client" && authStore.clientProfile?.first_entry?.required && !isPublicRoute.value) return false;
  if (authStore.isTaskOnlyInternalUser) return false;
  return true;
});

// Embedded route = custom app iframe
const isEmbeddedRoute = computed(() => {
  if (route.meta && route.meta.embedded === true) return true;
  return !!route.name && EMBEDDED_ROUTE_NAMES.indexOf(route.name) !== -1;
});

const toolLabel = computed(
  () => orchestrator.activeEmbedded?.label || "This app"
);

function dismissFullscreenHint() {
  showFullscreenHint.value = false;

  if (hintAutoHideTimer) {
    clearTimeout(hintAutoHideTimer);
    hintAutoHideTimer = null;
  }
}

function toggleSideNavigation() {
  isSidebarVisible.value = !isSidebarVisible.value;
}

function closeMobileMenu() {
  if (isMobileView.value) {
    isSidebarVisible.value = false;
  }
}

function clearFullScreenPillTimer() {
  if (fullScreenPillHideTimer) {
    clearTimeout(fullScreenPillHideTimer);
    fullScreenPillHideTimer = null;
  }
}

function enterFullScreenEmbedded() {
  fullScreenEmbedded.value = true;
  showFullScreenPill.value = true;
  exitCornerRevealed.value = false;

  clearFullScreenPillTimer();

  fullScreenPillHideTimer = setTimeout(() => {
    showFullScreenPill.value = false;
    fullScreenPillHideTimer = null;

    nextTick(() => {
      fullscreenFocusTrapRef.value?.focus();
    });
  }, 1500);

  nextTick(() => {
    setTimeout(() => exitFullScreenBtnRef.value?.focus(), 100);
  });
}

function exitFullScreenEmbedded() {
  clearFullScreenPillTimer();
  showFullScreenPill.value = false;
  exitCornerRevealed.value = false;
  fullScreenEmbedded.value = false;
}

function onKeydown(e) {
  if (e.key !== "Escape" || !fullScreenEmbedded.value) return;

  e.preventDefault();
  e.stopPropagation();

  exitFullScreenEmbedded();
}

watch(isEmbeddedRoute, (embedded) => {
  if (!embedded) {
    fullScreenEmbedded.value = false;
    showFullScreenPill.value = false;
    exitCornerRevealed.value = false;
    showFullscreenHint.value = true;

    clearFullScreenPillTimer();

    if (hintAutoHideTimer) {
      clearTimeout(hintAutoHideTimer);
      hintAutoHideTimer = null;
    }
  } else {
    showFullscreenHint.value = true;

    if (hintAutoHideTimer) clearTimeout(hintAutoHideTimer);

    hintAutoHideTimer = setTimeout(() => {
      showFullscreenHint.value = false;
      hintAutoHideTimer = null;
    }, 6000);
  }
});
</script>

<style lang="scss">
.main-layout {
  display: flex;
  flex: 1;
  position: relative;
}

.app--fixed-viewport {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}

.app--fixed-viewport .main-layout,
.app--fixed-viewport .main-column,
.app--fixed-viewport .main-content,
.app--fixed-viewport .router-view-wrapper,
.app--fixed-viewport .internal-viewport {
  min-height: 0;
  overflow: hidden;
}

/* Offset main column for fixed sidebar (desktop); syncs with sidebar width transition */
.main-column {
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --sidebar-offset: 0rem;
}

.main-column--sidebar-collapsed {
  margin-left: 4rem;
  --sidebar-offset: 4rem;
}

.main-column--sidebar-expanded {
  margin-left: 16rem;
  --sidebar-offset: 16rem;
}

.main-content {
  min-height: 0;
}

.main-content--no-page-scroll {
  overflow: hidden;
}

/* Mobile: sidebar overlays; main column is full width */
@media (max-width: 1023px) {
  .main-column--sidebar-collapsed,
  .main-column--sidebar-expanded {
    margin-left: 0;
    --sidebar-offset: 0rem;
  }
}

.mobile-overlay {
  pointer-events: auto;
}

/* Context transition: smooth switch between nav views */
.router-view-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.context-view {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.internal-viewport {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.context-enter-active,
.context-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.context-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

.context-enter-from {
  opacity: 0;
  transform: translateX(12px);
}

.context-enter-to,
.context-leave-from {
  opacity: 1;
  transform: translateX(0);
}

/* Full-screen embedded app: main content fills viewport */
.app--fullscreen-embedded .main-layout {
  position: fixed;
  inset: 0;
  z-index: 25;
}

.main-content--fullscreen {
  margin-left: 0 !important;
  padding-top: 0 !important;
  position: fixed;
  inset: 0;
  z-index: 26;
}

/* Sliding pill transition */
.slide-pill-enter-active,
.slide-pill-leave-active {
  transition: transform 0.3s ease, opacity 0.25s ease;
}

.slide-pill-enter-from,
.slide-pill-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-pill-enter-to,
.slide-pill-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* Right-side Full screen button */
.fade-slide-right-enter-active,
.fade-slide-right-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-slide-right-enter-from,
.fade-slide-right-leave-to {
  opacity: 0;
  transform: translateX(0.5rem);
}

.fade-slide-right-enter-to,
.fade-slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}

/* Full-screen mode: floating pill */
.embedded-fullscreen-pill {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: #343a40;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9999px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 0.875rem;
  white-space: nowrap;
}

.embedded-fullscreen-pill-text {
  display: inline-flex;
  align-items: center;
}

.embedded-fullscreen-pill-exit {
  padding: 0.35rem 0.75rem;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}

.embedded-fullscreen-pill-exit:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Invisible focus trap */
.embedded-fullscreen-focus-trap {
  position: fixed;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  outline: none;
  z-index: 9997;
}

/* Hot corner zone */
.embedded-fullscreen-exit-corner-zone {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9998;
  width: 10rem;
  height: 4.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0.5rem 1rem 0 0;
  pointer-events: auto;
}

/* Exit button */
.embedded-fullscreen-exit-corner {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.6rem;
  background: rgba(52, 58, 64, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transform: translateY(-100%);
  opacity: 0;
  transition: transform 0.25s ease, opacity 0.2s ease, background 0.15s ease;
  pointer-events: auto;
}

.embedded-fullscreen-exit-corner--revealed {
  transform: translateY(0);
  opacity: 1;
}

.embedded-fullscreen-exit-corner:hover {
  background: rgba(52, 58, 64, 1);
}

/* Normal mode: hint pill */
.embedded-fullscreen-hint-pill {
  position: fixed;
  top: 5rem;
  left: calc(50% + (var(--sidebar-offset, 0rem) / 2));
  transform: translateX(-50%);
  z-index: 25;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: #343a40;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9999px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  white-space: nowrap;
}

.embedded-fullscreen-hint-btn {
  padding: 0.35rem 0.75rem;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.5rem;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}

.embedded-fullscreen-hint-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.embedded-fullscreen-hint-dismiss {
  padding: 0.25rem;
  color: rgba(255, 255, 255, 0.7);
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: color 0.15s ease;
}

.embedded-fullscreen-hint-dismiss:hover {
  color: #fff;
}
</style>
